import React, { useEffect, useRef, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";

type CopyButtonSize = "xs" | "sm";
type CopyStatus = "idle" | "copied" | "error";

export interface CopyButtonProps {
  /** The string to copy, or a thunk resolving it on click — used when the
   *  caller only holds a truncated preview. Must be synchronous: WebKit
   *  rejects a clipboard write reached after an await. */
  text: string | (() => string);
  size?: CopyButtonSize;
  /** Defaults to "Copy to clipboard". */
  ariaLabel?: string;
  successDurationMs?: number;
  errorDurationMs?: number;
  /** Fired after a successful copy, e.g. to show a global notification. */
  onCopy?: () => void;
  className?: string;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  size = "xs",
  ariaLabel = "Copy to clipboard",
  successDurationMs = 1200,
  errorDurationMs = 1500,
  onCopy,
  className = "",
}) => {
  const [status, setStatus] = useState<CopyStatus>("idle");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    try {
      // WebKit rejects clipboard writes reached after an `await` in the
      // gesture task, so resolve the thunk synchronously and hand the promise
      // to writeText instead.
      const value = typeof text === "function" ? text() : text;
      await navigator.clipboard.writeText(value);
      setStatus("copied");
      onCopy?.();
      timeoutRef.current = setTimeout(
        () => setStatus("idle"),
        successDurationMs,
      );
    } catch {
      setStatus("error");
      timeoutRef.current = setTimeout(() => setStatus("idle"), errorDurationMs);
    }
  };

  const iconSize = size === "xs" ? 14 : 16;
  const icon =
    status === "copied" ? (
      <CheckIcon className="text-success" style={{ fontSize: iconSize }} />
    ) : status === "error" ? (
      <ErrorOutlineIcon className="text-error" style={{ fontSize: iconSize }} />
    ) : (
      <ContentCopyIcon style={{ fontSize: iconSize }} />
    );

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={ariaLabel}
      className={`inline-flex items-center justify-center w-6 h-6 rounded-app cursor-pointer text-fg hover:bg-bg-hover hover:text-fg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent transition-colors ${className}`}
    >
      {icon}
    </button>
  );
};
